"use client";

import CTAGroup from "@/components/CTAGroup";
import LazyIcon from "@/components/icons/LazyIcon";
import { useStickyBannerContext } from "@/context/AppContext";
import clientSide from "@/helpers/clientSide";
import useElementViewed from "@/hooks/tagular/use-element-viewed";
import cn from "@/lib/cn";
import { parseMetadata } from "@/lib/parse-metadata";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import React, { useEffect, useRef, useState } from "react";
import type { StickyOfferBannerProps } from "./types";

const StickyOfferBanner: React.FC<StickyOfferBannerProps> = ({
  elementName,
  icon,
  title,
  mobileTitle,
  desktopTitle,
  subtitle,
  mobileSubtitle,
  desktopSubtitle,
  ctaGroup,
  hasInlineTextLink = false,
  backgroundColor = "dark-purple-gradient",
  contentAlignment,
  className,
}) => {
  const mobileAlign = contentAlignment?.mobile ?? "left";
  const tabletAlign = contentAlignment?.tablet ?? "left";
  const desktopAlign = contentAlignment?.desktop ?? "left";
  const { setStickyBannerHeight } = useStickyBannerContext();
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isTabletOrDesktopViewport, setIsTabletOrDesktopViewport] = useState(
    () =>
      clientSide &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(min-width: 768px)").matches
  );
  const resolvedMobileTitleCopy = mobileTitle?.copy || title?.copy || "";
  const resolvedDesktopTitleCopy = desktopTitle?.copy || title?.copy || "";
  const resolvedMobileSubtitleCopy =
    mobileSubtitle?.copy || subtitle?.copy || "";
  const resolvedDesktopSubtitleCopy =
    desktopSubtitle?.copy || subtitle?.copy || "";
  const resolvedTrackingTitleCopy =
    (isTabletOrDesktopViewport
      ? resolvedDesktopTitleCopy
      : resolvedMobileTitleCopy) ||
    resolvedDesktopTitleCopy ||
    resolvedMobileTitleCopy;
  const showResponsiveTitle =
    Boolean(resolvedMobileTitleCopy) || Boolean(resolvedDesktopTitleCopy);
  const showResponsiveSubtitle =
    Boolean(resolvedMobileSubtitleCopy) || Boolean(resolvedDesktopSubtitleCopy);
  const hasDistinctResponsiveTitle =
    resolvedMobileTitleCopy !== resolvedDesktopTitleCopy;
  const hasDistinctResponsiveSubtitle =
    resolvedMobileSubtitleCopy !== resolvedDesktopSubtitleCopy;
  const isVisible = showResponsiveTitle && !isDismissed;

  const handleClose = () => {
    fireElementClicked({
      actionOutcome: "bannerClosed",
      elementType: "iconButton",
      location: "banner" as const,
      name: `iconButton${elementName ?? "StickyOfferBanner"}Close`,
      position: "",
      text: "close",
    });
    setIsDismissed(true);
  };

  useEffect(() => {
    if (!isVisible) {
      setStickyBannerHeight(0);
      return;
    }

    const element = bannerRef.current;
    if (!element) return;

    const updateHeight = () => {
      setStickyBannerHeight(element.getBoundingClientRect().height);
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeight);
      return () => {
        window.removeEventListener("resize", updateHeight);
        setStickyBannerHeight(0);
      };
    }

    const resizeObserver = new ResizeObserver(() => updateHeight());
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
      setStickyBannerHeight(0);
    };
  }, [isVisible, setStickyBannerHeight]);

  useEffect(() => {
    if (!clientSide || typeof window.matchMedia !== "function") return;

    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const updateViewportMode = (event: MediaQueryListEvent) => {
      setIsTabletOrDesktopViewport(event.matches);
    };

    mediaQuery.addEventListener("change", updateViewportMode);

    return () => {
      mediaQuery.removeEventListener("change", updateViewportMode);
    };
  }, []);

  useElementViewed(
    bannerRef,
    {
      elementType: "card",
      location: "banner" as const,
      name: elementName ?? "stickyOfferBanner",
      text: resolvedTrackingTitleCopy.replace(/<[^>]*>/g, ""),
      isInBounds: isVisible,
    },
    { threshold: 0.25 }
  );

  if (!isVisible) return null;

  const isLightTheme = backgroundColor === "light-theme-base";
  const isTransparentLight = backgroundColor === "transparent-light";
  const isTransparentDark = backgroundColor === "transparent-dark";
  const isTransparentTheme = isTransparentLight || isTransparentDark;
  const isDarkTheme = !isLightTheme && !isTransparentLight;
  const resolveDisplayForCta = (
    cta: NonNullable<StickyOfferBannerProps["ctaGroup"]>["ctas"][number]
  ) =>
    cta.display ??
    (cta.action === "tooltip"
      ? "textLink"
      : (ctaGroup?.defaultDisplay ?? "fill"));
  // Preserve original left-to-right position before splitting into groups
  // so each CTAGroup doesn't reset index-based tagular positions.
  const annotatedCtas =
    ctaGroup?.ctas?.map((cta, index) => ({
      ...cta,
      tagularProps: {
        ...cta.tagularProps,
        position: cta.tagularProps?.position ?? index + 1,
      },
    })) ?? [];
  const textLinkCtas = hasInlineTextLink
    ? annotatedCtas.filter((cta) => resolveDisplayForCta(cta) === "textLink")
    : [];
  const buttonCtas = hasInlineTextLink
    ? annotatedCtas.filter((cta) => resolveDisplayForCta(cta) !== "textLink")
    : annotatedCtas;
  const resolvedInlineTextLinkGroup = textLinkCtas.length
    ? {
        ...ctaGroup,
        ctas: textLinkCtas,
        elementName,
        location: "banner" as const,
        tagularProps: ctaGroup?.tagularProps,
        defaultDisplay: "textLink" as const,
        defaultTone:
          ctaGroup?.defaultTone ?? (isDarkTheme ? "inverse" : "neutral"),
        defaultSize: ctaGroup?.defaultSize ?? "sm",
        className: cn(
          "gap-1.5 flex-nowrap items-baseline",
          ctaGroup?.className
        ),
        buttonClassName: ctaGroup?.buttonClassName,
      }
    : undefined;
  const resolvedButtonCtaGroup = buttonCtas.length
    ? {
        ...ctaGroup,
        ctas: buttonCtas,
        elementName,
        location: "banner" as const,
        tagularProps: ctaGroup?.tagularProps,
        defaultDisplay: ctaGroup?.defaultDisplay ?? "fill",
        defaultTone:
          ctaGroup?.defaultTone ?? (isDarkTheme ? "inverse" : "neutral"),
        defaultSize: ctaGroup?.defaultSize ?? "xs",
        className: cn(
          "flex-wrap",
          tabletAlign === "center" ? "md:justify-center" : "md:justify-end",
          ctaGroup?.className
        ),
        buttonClassName: cn(
          "py-2 body-xs-semibold md:py-2.5 md:text-sm md:leading-6",
          ctaGroup?.buttonClassName
        ),
      }
    : undefined;
  const bannerBorderClass = isTransparentTheme
    ? ""
    : "border border-[rgba(90,35,185,0.15)]"; // Figma border color
  const bannerShadowClass = isTransparentTheme
    ? ""
    : "shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.1),0px_4px_6px_-1px_rgba(0,0,0,0.1)]"; // Figma drop shadow
  const bannerGradientClass =
    "bg-[linear-gradient(233.53deg,#7b30ff_0%,#5a23b9_50%,#1f0585_100%)] text-white"; // Figma gradient
  const bannerLightClass = "bg-material-theme1-base text-neutral-base";
  const bannerTransparentLightClass = "bg-gray-50/90 text-neutral-base";
  const bannerTransparentDarkClass =
    "bg-gradient-to-tr from-royal-purple/90 via-purple-500/90 to-purple-400/90 text-white";
  const titleClassName = cn(
    "title-tiny-semibold md:title-xs-semibold xl:text-lg-semibold",
    isDarkTheme ? "text-white" : "text-neutral-base"
  );
  const subtitleClassName = cn(
    "text-xs md:text-sm xl:text-base",
    isDarkTheme ? "text-white" : "text-neutral-base"
  );
  const iconTheme = isDarkTheme ? "white" : "black";
  const closeIconTheme = isDarkTheme ? "white" : "black";
  const closeIconClassName = cn(
    "absolute right-3 top-3 inline-flex items-center justify-center cursor-pointer",
    isDarkTheme ? "text-white" : "text-neutral-base"
  );

  return (
    <div ref={wrapperRef} className="relative">
      <div
        ref={bannerRef}
        className={cn("inset-x-0 z-50 absolute", className)}
        data-testid="sticky-offer-banner"
        style={{
          top: 0,
          paddingTop: "16px",
          paddingBottom: "12px",
          transition: "top 120ms ease-out",
        }}
      >
        <div className="mx-auto max-w-screen-2xl px-4 md:px-6 xl:px-16">
          <div
            className={cn(
              "relative flex flex-col gap-2 rounded-lg pl-3 pr-10 py-3 md:py-4 md:pl-6 md:pr-12",
              bannerBorderClass,
              bannerShadowClass,
              {
                [bannerLightClass]: isLightTheme,
                [bannerTransparentLightClass]: isTransparentLight,
                [bannerTransparentDarkClass]: isTransparentDark,
                [bannerGradientClass]:
                  !isLightTheme && !isTransparentLight && !isTransparentDark,
              },
              "md:flex-row md:items-center",
              mobileAlign === "center" ? "items-center" : "",
              tabletAlign === "center"
                ? "md:justify-center"
                : "md:justify-between",
              desktopAlign === "center"
                ? "xl:justify-center"
                : "xl:justify-between"
            )}
          >
            <div
              className={cn(
                "flex flex-1 flex-col md:flex-row flex-wrap md:items-center gap-3",
                mobileAlign === "center" ? "items-center text-center" : "",
                tabletAlign === "center"
                  ? "md:items-center md:text-center"
                  : "",
                desktopAlign === "center"
                  ? "xl:items-center xl:text-center"
                  : ""
              )}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                {icon?.assetKey || showResponsiveTitle ? (
                  <div
                    className={cn(
                      "flex flex-1 items-center gap-2",
                      mobileAlign === "center" ? "justify-center" : "",
                      tabletAlign === "center" ? "md:justify-center" : "",
                      desktopAlign === "center" ? "xl:justify-center" : ""
                    )}
                  >
                    {icon?.assetKey ? (
                      <div className="flex shrink-0 items-start justify-center min-w-6 min-h-6">
                        <LazyIcon
                          name={icon.assetKey}
                          className="w-6 h-6"
                          style="outline"
                          theme={iconTheme}
                          title={icon.alt}
                        />
                      </div>
                    ) : null}
                    {showResponsiveTitle && !hasDistinctResponsiveTitle ? (
                      <p
                        className={titleClassName}
                        dangerouslySetInnerHTML={{
                          __html: parseMetadata(
                            resolvedDesktopTitleCopy || resolvedMobileTitleCopy
                          ),
                        }}
                      />
                    ) : null}
                    {showResponsiveTitle && hasDistinctResponsiveTitle ? (
                      <>
                        {resolvedMobileTitleCopy ? (
                          <p
                            className={cn(titleClassName, "md:hidden")}
                            dangerouslySetInnerHTML={{
                              __html: parseMetadata(resolvedMobileTitleCopy),
                            }}
                          />
                        ) : null}
                        {resolvedDesktopTitleCopy ? (
                          <p
                            className={cn(titleClassName, "hidden md:block")}
                            dangerouslySetInnerHTML={{
                              __html: parseMetadata(resolvedDesktopTitleCopy),
                            }}
                          />
                        ) : null}
                      </>
                    ) : null}
                  </div>
                ) : null}
                {showResponsiveSubtitle || resolvedInlineTextLinkGroup ? (
                  <div
                    className={cn(
                      "flex min-w-0 flex-nowrap items-baseline gap-x-2 gap-y-1",
                      mobileAlign === "center" ? "justify-center" : "",
                      tabletAlign === "center" ? "md:justify-center" : "",
                      desktopAlign === "center" ? "xl:justify-center" : ""
                    )}
                  >
                    <p className={subtitleClassName}>
                      {showResponsiveSubtitle &&
                      !hasDistinctResponsiveSubtitle ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: parseMetadata(
                              resolvedDesktopSubtitleCopy ||
                                resolvedMobileSubtitleCopy
                            ),
                          }}
                        />
                      ) : null}
                      {showResponsiveSubtitle &&
                      hasDistinctResponsiveSubtitle ? (
                        <>
                          {resolvedMobileSubtitleCopy ? (
                            <span
                              className="md:hidden"
                              dangerouslySetInnerHTML={{
                                __html: parseMetadata(
                                  resolvedMobileSubtitleCopy
                                ),
                              }}
                            />
                          ) : null}
                          {resolvedDesktopSubtitleCopy ? (
                            <span
                              className="hidden md:inline"
                              dangerouslySetInnerHTML={{
                                __html: parseMetadata(
                                  resolvedDesktopSubtitleCopy
                                ),
                              }}
                            />
                          ) : null}
                        </>
                      ) : null}
                      {resolvedInlineTextLinkGroup ? (
                        <CTAGroup
                          {...resolvedInlineTextLinkGroup}
                          className="inline pl-2"
                        />
                      ) : null}
                    </p>
                  </div>
                ) : null}
              </div>
              {resolvedButtonCtaGroup ? (
                <div
                  className={cn(
                    "flex w-full md:w-auto items-center",
                    mobileAlign === "center" ? "justify-center" : "",
                    tabletAlign === "center" ? "md:justify-center" : "",
                    desktopAlign === "center" ? "xl:justify-center" : ""
                  )}
                >
                  <CTAGroup {...resolvedButtonCtaGroup} />
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className={closeIconClassName}
              onClick={handleClose}
              aria-label="Dismiss offer banner"
            >
              <LazyIcon
                name="IconClose"
                className="w-5 h-5"
                style="outline"
                theme={closeIconTheme}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StickyOfferBanner;
